import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ConsumablePermissions,
  DailyLegLoadPermissions,
  DailyLogPermissions,
  DailyObservationPermissions,
  JackingPermissions,
  LockedDayPermissions,
  MorningMinutesPermissions,
  ProjectDetailsPermissions,
  SafetyPermissions,
  SidebarPermissions,
  VesselCrewPermissions,
  VoyageLogPermissions,
  WeatherPermissions
} from '../../models/permissions/permission.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { LockedDayPermissionService } from '../implementer/locked-day-permission.service';
import { CompleteDayService } from '../complete-day/complete-day.service';
import { AppConfig } from '../../app/app.config';

@Injectable()
export class PermissionsService {
  config = AppConfig.settings;
  private sidebarPermissionsCache: SidebarPermissions | null = null;

  constructor(
    private http: HttpClient,
    private lockedDayPermissionService: LockedDayPermissionService,
    private completeDayService: CompleteDayService,
  ) {
  }

  sidebarPermissions(forceRefresh: boolean = false): Promise<SidebarPermissions> {
    if (this.sidebarPermissionsCache && !forceRefresh) {
      return Promise.resolve(this.sidebarPermissionsCache);
    }

    const url = `${this.config.apiUrl}api/sidebar/permissions`;
    return this.http
      .get<SidebarPermissions>(url)
      .toPromise()
      .then(res => {
        this.sidebarPermissionsCache = res;
        return Promise.resolve(res);
      }).catch(err => {
        const fallback = new SidebarPermissions();
        this.sidebarPermissionsCache = fallback;
        return Promise.resolve(fallback);
      });
  }

  getCachedSidebarPermissions(): SidebarPermissions | null {
    return this.sidebarPermissionsCache;
  }

  weatherPermissions(): Promise<WeatherPermissions> {
    const url = `${this.config.apiUrl}api/weatherreading/permissions`;
    return this.http
      .get<WeatherPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new WeatherPermissions());
      });
  }

  dailyLegLoadPermissions(): Promise<DailyLegLoadPermissions> {
    const url = `${this.config.apiUrl}api/dailylegload/permissions`;
    return this.http
      .get<DailyLegLoadPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new DailyLegLoadPermissions());
      });
  }

  projectDetailsPermissions(): Promise<ProjectDetailsPermissions> {
    const url = `${this.config.apiUrl}api/projects/permissions`;
    return this.http
      .get<ProjectDetailsPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new ProjectDetailsPermissions());
      });
  }

  vesselCrewPermissions(): Promise<VesselCrewPermissions> {
    const url = `${this.config.apiUrl}api/vesselcrew/permissions`;
    return this.http
      .get<VesselCrewPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new VesselCrewPermissions());
      });
  }

  safetyPermissions(): Promise<SafetyPermissions> {
    const url = `${this.config.apiUrl}api/safety/permissions`;
    return this.http
      .get<SafetyPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new SafetyPermissions());
      });
  }

  jackingPermissions(): Promise<JackingPermissions> {
    const url = `${this.config.apiUrl}api/jacking/permissions`;
    return this.http
      .get<JackingPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new JackingPermissions());
      });
  }

  dailyObservationPermissions(): Promise<DailyObservationPermissions> {
    const url = `${this.config.apiUrl}api/observations/permissions`;
    return this.http
      .get<DailyObservationPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new DailyObservationPermissions());
      });
  }

  consumablePermissions(): Promise<ConsumablePermissions> {
    const url = `${this.config.apiUrl}api/consumables/permissions`;
    return this.http
      .get<ConsumablePermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new ConsumablePermissions());
      });
  }

  dailyLogPermissions(): Promise<DailyLogPermissions> {
    const url = `${this.config.apiUrl}api/dailylog/permissions`;
    return this.http
      .get<DailyLogPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new DailyLogPermissions());
      });
  }

  voyageLogPermissions(): Promise<VoyageLogPermissions> {
    const url = `${this.config.apiUrl}api/voyagelog/permissions`;
    return this.http
      .get<VoyageLogPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new VoyageLogPermissions());
      });
  }

  morningMinutesPermissions(): Promise<MorningMinutesPermissions> {
    const url = `${this.config.apiUrl}api/morningminutes/permissions`;
    return this.http
      .get<MorningMinutesPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new MorningMinutesPermissions());
      });
  }

  async lockedDayPermissions(dailyLogDate?: PtLuxonDate): Promise<LockedDayPermissions> {
    const lockedDayPermissions = await this.getLockedDayPermissions();
    await this.lockedDayPermissionService.setFunctionalPermissions(lockedDayPermissions, dailyLogDate);

    return lockedDayPermissions;
  }

  // as functional permissions are based on contextual data, we need to update these on things like daily log date changes, but there's no
  // need to call the api again and re-get the normal permissions, so just re-process the functional ones with the new dailyLogDate
  async updateLockedDayFunctionalPermissions(lockedDayPermissions: LockedDayPermissions, dailyLogDate: PtLuxonDate) {
    await this.lockedDayPermissionService.setFunctionalPermissions(lockedDayPermissions, dailyLogDate);
  }

  private getLockedDayPermissions(): Promise<LockedDayPermissions> {
    const url = `${this.config.apiUrl}api/implementer/permissions`;
    return this.http
      .get<LockedDayPermissions>(url)
      .toPromise()
      .then(res => {
        return Promise.resolve(res);
      }).catch(err => {
        return Promise.resolve(new LockedDayPermissions());
      });
  }
}



// WEBPACK FOOTER //
// ./src/services/permissions/permissions.service.ts